import { UploadFile } from "antd/es/upload/interface";

import { FileRepositoryUploadWithHook } from "./editors";
import {
  ContentTypeRelations,
  FieldDependencies,
  FormChoiceOption,
} from "@/domains/datablocks/types";
import { Roles } from "@/shared/utils/constants";
import {
  useGetChoiceOptionsQuery,
  useGetDataFilesQuery,
  useGetSubjectsQuery,
  useGetUsersQuery,
  useGetChaptersQuery,
  useGetLanguagesQuery,
  useGetCountriesQuery,
} from "@/redux/service";
import { TagsElement } from "@/components_v2/ContentElements/TagsElement";
import TextElement from "../../../components_v2/ContentElements/TextElement";
import {
  FileElement,
  FileGroupElement,
} from "@/components_v2/ContentElements/FileElement";
import SliderDropdownElement from "../../../components_v2/ContentElements/SliderDropdownElement";
import ChapterElement from "../../../components_v2/ContentElements/ChapterElement";
import ChapterElementGroup from "../../../components_v2/ContentElements/ChapterElementGroup";
import {
  HookFormBaseDropdown,
  HookFormCheckboxGroup,
  HookFormCustomChoiceDropdown,
  HookFormInfiniteScrollCustomChoiceDropdown,
  HookFormInfiniteScrollDropdown,
  HookFormSliderDropdown,
} from "@/components_v2/FormFields/ReactHookForm/fields";
import {
  BaseFile,
  UploadFunctionReturnProps,
} from "@/components_v2/FormFields/Upload/types";
import { FileUploadError } from "@/components_v2/FormFields/Upload/errors";

import { DataFileResponse } from "@/redux/files/types";
import { CountryResponse, LanguageResponse } from "@/redux/common/types";
import { SubjectResponse } from "@/redux/organizations/types";
import { ChapterResponse } from "@/redux/chapters/types";
import {
  ChoiceOptionResponse,
  ContentType,
  DataFieldResponse,
  DisplayType,
  ValidationRuleTypes,
} from "@/redux/datablocks/types";
import { BaseUserResponse } from "@/redux/users/types";

const sortByIdOrOrder = (a: ChoiceOptionResponse, b: ChoiceOptionResponse) => {
  if (a.order !== undefined && b.order !== undefined) {
    return a.order - b.order;
  } else {
    return a.id - b.id;
  }
};

const getOptions = (options: ChoiceOptionResponse[]): FormChoiceOption[] => {
  return [...options].sort(sortByIdOrOrder).map((option) => {
    const enabledRelatedOptions = (option?.relatedOptions || []).filter(
      (relatedOption) => !relatedOption.disabled,
    );

    const processedRelatedOptions = getOptions(enabledRelatedOptions);

    return {
      label: option.label,
      relatedOptions: processedRelatedOptions,
      value: option.id,
      category: option?.category,
    };
  });
};

export const CONTENT_TYPE_PROPS = {
  [ContentType.Options]: {
    query: {
      useQuery: useGetChoiceOptionsQuery,
    },
    rendererProps: (field: DataFieldResponse, data: ContentTypeRelations) => {
      const options = data as ChoiceOptionResponse[];
      if (field.multiple) {
        return {
          value: options,
        };
      }

      const selectedOption = options[0];
      return { value: selectedOption ? selectedOption.label : undefined };
    },
    rendererComponent: (field: DataFieldResponse) => {
      if (field.multiple) {
        return TagsElement;
      } else if (field.displayType === DisplayType.DROPDOWN_WITH_SCALE) {
        return SliderDropdownElement;
      }
      return TextElement;
    },
    editorComponent: (field: DataFieldResponse) => {
      if (field.displayType === DisplayType.DROPDOWN_WITH_SCALE) {
        return HookFormSliderDropdown;
      }
      if (field.displayType === DisplayType.CHECKBOX) {
        return HookFormCheckboxGroup;
      }
      if (field.allowsCustomOptions) {
        return HookFormCustomChoiceDropdown;
      }
      return HookFormBaseDropdown;
    },
    editorProps: (
      field: DataFieldResponse,
      dependencies: FieldDependencies,
    ) => {
      const { dataTemplate } = dependencies;
      const fieldIndexMap = dataTemplate.templateFields.reduce<
        Record<number, number>
      >((acc, field, index) => {
        acc[field.id] = index;
        return acc;
      }, {});

      return {
        options: getOptions(field?.options || []),
        constraints: (field?.constraints || []).map((constraint) => ({
          fieldName: `fieldValues.${fieldIndexMap[constraint.parentField]}.value`,
          allowedOptions: constraint.allowedChildOptions,
          parentOption: constraint.parentOption,
          affectsMainOptions: constraint.affectsMainOptions,
        })),
      };
    },
  },

  [ContentType.Users]: {
    query: {
      useQuery: useGetUsersQuery,
    },
    rendererProps: (field: DataFieldResponse, data: ContentTypeRelations) => {
      const users = data as BaseUserResponse[];
      if (!field.multiple) {
        const selectedUser = users[0];
        return {
          value: selectedUser
            ? {
                label: selectedUser.fullName,
                link: `/users/${selectedUser.id}`,
              }
            : undefined,
        };
      }
      return {
        value: users.map((user) => ({
          label: user.fullName,
          link: `/users/${user.id}`,
        })),
      };
    },
    rendererComponent: (field: DataFieldResponse) => {
      if (field.multiple) {
        return TagsElement;
      }
      return TextElement;
    },
    editorComponent: () => HookFormInfiniteScrollDropdown,
    editorProps: (_: DataFieldResponse, dependencies: FieldDependencies) => {
      const { getUsers, currentUser } = dependencies;
      const userOrganizationFilterParams =
        currentUser && currentUser.role !== Roles.ADMIN.value
          ? { organizations: currentUser.organization?.id }
          : {};

      return {
        fetchFn: getUsers,
        labelFieldName: "fullName",
        valueFieldName: "id",
        searchFieldName: "search",
        defaultFilters: userOrganizationFilterParams,
      };
    },
  },

  [ContentType.Subjects]: {
    query: {
      useQuery: useGetSubjectsQuery,
    },
    rendererProps: (field: DataFieldResponse, data: ContentTypeRelations) => {
      const subjects = data as SubjectResponse[];
      if (field.multiple) {
        return {
          value: subjects.map((subject) => ({ label: subject.name })),
        };
      }

      const selectedOption = subjects[0];
      return { value: selectedOption ? selectedOption.name : undefined };
    },
    rendererComponent: (field: DataFieldResponse) => {
      if (field.multiple) {
        return TagsElement;
      }
      return TextElement;
    },
    editorComponent: () => HookFormInfiniteScrollCustomChoiceDropdown,
    editorProps: (_: DataFieldResponse, dependencies: FieldDependencies) => {
      const { getSubjects, currentUser } = dependencies;
      const userOrganizationFilterParams =
        currentUser && currentUser.role !== Roles.ADMIN.value
          ? { organization: currentUser.organization?.id }
          : {};

      return {
        fetchFn: getSubjects,
        labelFieldName: "name",
        valueFieldName: "id",
        searchFieldName: "name",
        defaultFilters: userOrganizationFilterParams,
      };
    },
  },

  [ContentType.Files]: {
    query: {
      useQuery: useGetDataFilesQuery,
    },
    rendererProps: (field: DataFieldResponse, data: ContentTypeRelations) => {
      const files = data as DataFileResponse[];
      if (!field.multiple) {
        const selectedFile = files[0];
        return {
          value: selectedFile
            ? {
                file: selectedFile.file,
                name: selectedFile.name,
                downloadUrl: selectedFile.downloadUrl,
              }
            : undefined,
        };
      }
      return {
        value: files.map((file) => ({
          file: file.file,
          name: file.name,
          downloadUrl: file.downloadUrl,
        })),
      };
    },
    rendererComponent: (field: DataFieldResponse) => {
      if (field.multiple) {
        return FileGroupElement;
      }
      return FileElement;
    },
    editorComponent: () => FileRepositoryUploadWithHook,
    editorProps: (
      field: DataFieldResponse,
      dependencies: FieldDependencies,
    ) => {
      const { getDataFiles, createDataFile, currentUser } = dependencies;
      const userOrganizationFilterParams = currentUser
        ? { organization: currentUser.organization?.id }
        : {};

      const getRuleValue = (ruleType: ValidationRuleTypes) =>
        field?.rules?.find((rule) => rule.type === ruleType)?.value;

      const fileExtensions = getRuleValue(
        ValidationRuleTypes.ALLOWED_FILE_TYPES,
      );
      const maxFileSize = getRuleValue(ValidationRuleTypes.MAX_FILE_SIZE);

      const uploadFiles = async (
        files: UploadFile[],
      ): Promise<UploadFunctionReturnProps> => {
        const results = await Promise.allSettled(
          files.map(async (file) => {
            const response = await createDataFile({
              organization: currentUser?.organization?.id,
              name: file.name,
              file,
            });

            if (response?.error) {
              throw new FileUploadError(
                response.error.data.errors[0].detail,
                response.status,
                file,
              );
            }

            return {
              ...response.data,
              oldId: file.uid,
            };
          }),
        );

        const successful: BaseFile[] = [];
        const failed: FileUploadError[] = [];

        results.forEach((result, index) => {
          if (result.status === "fulfilled") {
            successful.push(result.value);
          } else {
            if (result.reason instanceof FileUploadError) {
              const error = result.reason as FileUploadError;
              failed.push(error);
            } else {
              failed.push(
                new FileUploadError(
                  result.reason?.message || "Unknown error",
                  500,
                  files[index] as UploadFile,
                ),
              );
            }
          }
        });

        return { successful, failed };
      };

      const fetchFilesWithFilters = (queryParams: Record<string, string>) => {
        const searchParams = {
          ...queryParams,
          extensions: "",
          editable: true,
          ...userOrganizationFilterParams,
        };

        if (fileExtensions) {
          searchParams.extensions = (fileExtensions as string[]).join(",");
        }

        return getDataFiles(searchParams);
      };

      return {
        fetchFn: fetchFilesWithFilters,
        labelFieldName: "name",
        valueFieldName: "id",
        supportedExtensions: fileExtensions,
        maxFileSize,
        uploadFn: uploadFiles,
      };
    },
  },

  [ContentType.Chapters]: {
    query: {
      useQuery: useGetChaptersQuery,
    },
    rendererProps: (field: DataFieldResponse, data: ContentTypeRelations) => {
      const chapters = data as ChapterResponse[];

      if (field.multiple) {
        return { value: chapters };
      }

      return { value: chapters[0] };
    },
    rendererComponent: (field: DataFieldResponse) => {
      if (field.multiple) {
        return ChapterElementGroup;
      }
      return ChapterElement;
    },
    editorComponent: () => HookFormInfiniteScrollCustomChoiceDropdown,
    editorProps: (_: DataFieldResponse, dependencies: FieldDependencies) => {
      const { getChapters, currentUser } = dependencies;
      const organizationFilterParams =
        currentUser && currentUser.role !== Roles.ADMIN.value
          ? { organization: currentUser.organization?.id }
          : {};

      return {
        fetchFn: getChapters,
        labelFieldName: "title",
        valueFieldName: "id",
        searchFieldName: "title",
        defaultFilters: organizationFilterParams,
      };
    },
  },

  [ContentType.Languages]: {
    query: {
      useQuery: useGetLanguagesQuery,
    },
    rendererProps: (field: DataFieldResponse, data: ContentTypeRelations) => {
      const languages = data as LanguageResponse[];
      if (field.multiple) {
        return {
          value: languages.map((language) => ({ label: language.name })),
        };
      }

      return { value: { label: languages[0]?.name } };
    },
    rendererComponent: (field: DataFieldResponse) => {
      if (field.multiple) {
        return TagsElement;
      }
      return TextElement;
    },
    editorComponent: () => HookFormInfiniteScrollDropdown,
    editorProps: (_: DataFieldResponse, dependencies: FieldDependencies) => {
      const { getLanguages } = dependencies;

      return {
        fetchFn: getLanguages,
        labelFieldName: "name",
        valueFieldName: "id",
        searchFieldName: "name",
      };
    },
  },

  [ContentType.Countries]: {
    query: {
      useQuery: useGetCountriesQuery,
    },
    rendererProps: (field: DataFieldResponse, data: ContentTypeRelations) => {
      const countries = data as CountryResponse[];

      if (field.multiple) {
        return { value: countries.map((country) => country.name) };
      }

      return { value: countries[0]?.name };
    },
    rendererComponent: (field: DataFieldResponse) => {
      if (field.multiple) {
        return TagsElement;
      }
      return TextElement;
    },
    editorComponent: () => HookFormInfiniteScrollDropdown,
    editorProps: (_: DataFieldResponse, dependencies: FieldDependencies) => {
      const { getCountries } = dependencies;

      return {
        fetchFn: getCountries,
        labelFieldName: "name",
        valueFieldName: "id",
        searchFieldName: "name",
      };
    },
  },
};

const getContentTypeProp = (
  propName: string,
  contentType?: ContentType,
  ...args: any
) => {
  if (!contentType) return;

  const configForType = CONTENT_TYPE_PROPS[contentType];

  if (!configForType) {
    throw new Error(`Unknown content type ${contentType}`);
  }

  // @ts-expect-error must exist
  const propOrFn = configForType[propName];

  if (typeof propOrFn === "function") {
    return propOrFn(...args);
  }

  return propOrFn;
};

export const getEditorComponentForContentType = (field: DataFieldResponse) =>
  getContentTypeProp("editorComponent", field.contentType, field);

export const getRendererComponentForContentType = (field: DataFieldResponse) =>
  getContentTypeProp("rendererComponent", field.contentType, field);

export const getEditorPropsForContentType = (
  field: DataFieldResponse,
  dependencies: FieldDependencies,
) => getContentTypeProp("editorProps", field.contentType, field, dependencies);

export const getRendererPropsForContentType = (
  field: DataFieldResponse,
  data: ContentTypeRelations,
) => getContentTypeProp("rendererProps", field.contentType, field, data);

export const getQueryPropsForContentType = (contentType: ContentType) =>
  getContentTypeProp("query", contentType);
